// The calibration file as the running app sees it: loaded once, applied to a live estimate, and
// summarised for the status line under the instructor's Jev toggle.
//
// `data/jev-calibration.json` is a static import rather than a fetch on purpose. It is small, it is
// part of the build, and a missing or half-written file must not be able to break a fight: the version
// that ships has `runs: 0`, which every reader here treats as "no calibration yet". The harness
// (`tools/jev-calibrate.mts`) overwrites it in place.

import file from '../../data/jev-calibration.json';
import {
  applyMap, argmax, calibrateDistribution, calibrationIsPresent, clampAwayFromCertainty, confidenceOf,
  floorDistribution, IDENTITY_MAP,
  type CalibrationFile, type CalibrationMap, type QuestionCalibration,
} from './calibrationMath';

/** The three judgments the estimator seam exposes. Question ids are ours, not the model's — Jev is told
 * the whole meaning in the question text, per https://docs.typesafe.ai/primitives. */
export type EstimatorQuestion = 'mainEffortThird' | 'contactRole' | 'reserveCommitSoon';

export const ESTIMATOR_QUESTIONS: readonly EstimatorQuestion[] = [
  'mainEffortThird', 'contactRole', 'reserveCommitSoon',
] as const;

/** The shipped file, typed. The JSON is validated structurally at the edges rather than with zod: every
 * reader below tolerates a missing question, a missing map and a missing provider. */
const CALIBRATION = file as unknown as CalibrationFile;

export function calibrationFile(): CalibrationFile {
  return CALIBRATION;
}

/** What the toggle's status line says about the calibration: present, when, and on how many fights. */
export interface CalibrationStatus {
  present: boolean;
  generatedAt: string | null;
  runs: number;
  samples: number;
  /** "calibrated on 2,000 fights" or "uncalibrated". */
  badge: string;
}

export function calibrationStatus(f: CalibrationFile = CALIBRATION): CalibrationStatus {
  const present = calibrationIsPresent(f);
  return {
    present,
    generatedAt: f.generatedAt,
    runs: f.runs,
    samples: f.samples,
    badge: present ? `calibrated on ${f.runs.toLocaleString()} fights` : 'uncalibrated',
  };
}

/** The fit for one question and provider, or undefined when the harness has not covered it. */
export function mapFor(
  question: EstimatorQuestion,
  provider: 'heuristic' | 'jev',
  f: CalibrationFile = CALIBRATION,
): QuestionCalibration | undefined {
  if (!calibrationIsPresent(f)) return undefined;
  return f.questions?.[question]?.[provider];
}

/** What one recalibration did, so the caller can set `calibrated` honestly rather than optimistically. */
export interface CalibratedDistribution {
  value: string;
  probabilities: Record<string, number>;
  confidence: number;
  /** False when no map existed for this question and provider — the numbers passed through untouched. */
  calibrated: boolean;
  map: CalibrationMap;
}

/**
 * Applies the fitted map for `question`/`provider` to a raw distribution.
 *
 * The chosen option is recomputed after the map rather than carried over: a monotone map applied
 * per-option and renormalised can, in principle, reorder two options that were within a hair of each
 * other, and the answer the interface shows should be the one the calibrated numbers support.
 */
export function calibrateChoice(
  question: EstimatorQuestion,
  provider: 'heuristic' | 'jev',
  probabilities: Record<string, number>,
  f: CalibrationFile = CALIBRATION,
): CalibratedDistribution {
  const fit = mapFor(question, provider, f);
  const map = fit?.map ?? IDENTITY_MAP;
  // Floored on both paths, calibrated or not: an option at exactly 0 is a claim of impossibility that
  // neither a fitted isotonic step nor a lopsided heuristic has earned (see `PROB_FLOOR`).
  const probs = floorDistribution(fit ? calibrateDistribution(map, probabilities) : probabilities);
  return {
    value: argmax(probs),
    probabilities: probs,
    confidence: confidenceOf(probs),
    calibrated: Boolean(fit),
    map,
  };
}

/** The yes/no counterpart. A Noul carries no confidence of its own (the docs are explicit: "no separate
 * confidence field"), so one is synthesised from the two-outcome distribution with Jev's own formula,
 * which is what makes a Noul and a Choice comparable in one interface. */
export function calibrateNoul(
  question: EstimatorQuestion,
  provider: 'heuristic' | 'jev',
  p: number,
  f: CalibrationFile = CALIBRATION,
): { p: number; confidence: number; calibrated: boolean; map: CalibrationMap } {
  const fit = mapFor(question, provider, f);
  const map = fit?.map ?? IDENTITY_MAP;
  const out = clampAwayFromCertainty(fit ? applyMap(map, p) : p);
  return {
    p: out,
    confidence: confidenceOf({ yes: out, no: 1 - out }),
    calibrated: Boolean(fit),
    map,
  };
}
